import React, { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiX, FiCheck} from "react-icons/fi";

const CustomDropdown = ({
  options = [],
  value, // Can be string/number OR array if isMulti is true
  onChange,
  placeholder = "Select",
  optionLabel = "label",
  optionValue = "value",
  className = "",
  searchable = true,
  isJobList = false,
  isMulti = false, // New prop
  compactMultiDisplay = false,
  multiDisplayLimit = 2,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const getOptionLabel = (opt) => {
    if (!opt) return "";
    const baseLabel =
      opt[optionLabel] ||
      opt.name ||
      opt.country_name ||
      opt.language ||
      opt.code ||
      opt.job_title ||
      opt;

    if (isJobList && opt.job_title && opt.mph_jobid) {
      return `${opt.job_title} - ${opt.mph_jobid}`;
    }
    return baseLabel;
  };

  const getOptionValue = (opt) => {
    return opt[optionValue] ?? opt.value ?? opt.code ?? opt.country_guid ?? opt;
  };

  const handleSelect = (e, val) => {
    e.stopPropagation();
    if (isMulti) {
      const currentValues = Array.isArray(value) ? value : [];
      if (currentValues.includes(val)) {
        onChange(currentValues.filter((v) => v !== val));
      } else {
        onChange([...currentValues, val]);
      }
      // Keep open for multi-select
    } else {
      onChange(val);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const removeValue = (e, val) => {
    e.stopPropagation();
    const currentValues = Array.isArray(value) ? value : [];
    onChange(currentValues.filter((v) => v !== val));
  };

  const filteredOptions = options?.filter((opt) => {
    let label = getOptionLabel(opt);
    const searchableString = isJobList
      ? `${opt.job_title || ""} ${opt.mph_jobid || ""}`
      : label;
    return searchableString.toString().toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Determine what to show in the closed box
  const renderDisplayValue = () => {
    if (isMulti && Array.isArray(value) && value.length > 0) {
      const selectedItems = value.map((v) => {
        const opt = options.find((o) => getOptionValue(o) === v);
        return {
          value: v,
          label: opt ? getOptionLabel(opt) : v,
        };
      });

      if (compactMultiDisplay) {
        const visibleItems = selectedItems.slice(0, multiDisplayLimit);
        const hiddenItems = selectedItems.slice(multiDisplayLimit);
        const hiddenCount = hiddenItems.length;
        const hiddenLabels = hiddenItems.map((item) => item.label).join(", ");

        return (
          <div className="flex flex-wrap gap-1 items-center overflow-hidden">
            {visibleItems.map((item) => (
              <span key={item.value} className="bg-gray-50 text-gray-700 px-1.5 py-0.5 rounded-md flex items-center gap-1 border border-gray-100 max-w-[140px]">
                <span className="truncate">{item.label}</span>
                <FiX className="cursor-pointer hover:text-red-500" onClick={(e) => removeValue(e, item.value)} />
              </span>
            ))}
            {hiddenCount > 0 && (
              <span
                className="bg-gray-50 text-gray-700 px-1.5 py-0.5 rounded-md border border-gray-100 cursor-default"
                title={hiddenLabels}
              >
                +{hiddenCount}
              </span>
            )}
          </div>
        );
      }

      return (
        <div className="flex flex-wrap gap-1 items-center overflow-hidden">
          {selectedItems.map((item) => {
            return (
              <span key={item.value} className="bg-gray-50 text-gray-700 px-1.5 py-0.5 rounded-md flex items-center gap-1 border border-gray-100 max-w-[120px]">
                <span className="truncate">{item.label}</span>
                <FiX className="cursor-pointer hover:text-red-500" onClick={(e) => removeValue(e, item.value)} />
              </span>
            );
          })}
        </div>
      );
    }

    if (!isMulti && value) {
      const selectedOpt = options.find((opt) => getOptionValue(opt) === value);
      const label = selectedOpt ? getOptionLabel(selectedOpt) : value;
      return <span className="text-gray-800 truncate">{typeof label === 'object' ? label.job_title : label}</span>;
    }

    return <span className="text-gray-400">{placeholder}</span>;
  };

  return (
    <div ref={dropdownRef} className={`relative text-xs w-full ${className}`}>
      <div
        className={`border border-gray-300 rounded px-2 py-1.5 w-full flex justify-between items-center bg-white min-h-[36px] cursor-pointer transition-all ${isOpen ? "ring-1 ring-blue-400" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex-1 flex overflow-hidden">
          {isOpen && searchable ? (
            <input
              ref={inputRef}
              type="text"
              className="w-full bg-transparent border-none outline-none focus:ring-0 p-0 text-xs"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            renderDisplayValue()
          )}
        </div>
        <FiChevronDown className={`ml-2 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </div>

      <div
        className={`absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg transition-all duration-200 origin-top ${isOpen ? "opacity-100 scale-y-100 max-h-[250px]" : "opacity-0 scale-y-95 max-h-0 pointer-events-none"}`}
      >
        <ul className="overflow-y-auto max-h-[200px] py-1">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, idx) => {
              const val = getOptionValue(opt);
              const label = getOptionLabel(opt);
              const isSelected = isMulti 
                ? Array.isArray(value) && value.includes(val)
                : value === val;

              return (
                <li
                  key={idx}
                  className={`px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${isSelected ? "bg-gray-50 text-gray-900 font-semibold" : "text-gray-700"}`}
                  onClick={(e) => handleSelect(e, val)}
                >
                  <span className="truncate">{label}</span>
                  {isSelected && isMulti && <FiCheck className="w-3 h-3 rounded-full text-green-500" />}
                </li>
              );
            })
          ) : (
            <li className="px-3 py-2 text-gray-400 italic text-center">No results</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default CustomDropdown;